import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { docSections as staticSections, type DocSection } from "@/content/docs";
import {
  BookOpen, Zap, CheckCircle2, Users, FileText, CreditCard, Code, Lightbulb,
  Settings, Shield, Globe, Database, Wrench, HelpCircle,
} from "lucide-react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen, Zap, CheckCircle2, Users, FileText, CreditCard, Code, Lightbulb,
  Settings, Shield, Globe, Database, Wrench, HelpCircle,
};

function resolveIcon(name: string): React.ComponentType<{ className?: string }> {
  return iconMap[name] || FileText;
}

// Icon name stored in DB as string, mapped on render side
export interface DbDocSection {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  content: string;
  sort_order: number;
  published: boolean;
  created_at: string;
  updated_at: string;
}

/** Merge DB sections over static defaults (DB wins by slug) */
function mergeWithStatic(dbSections: DbDocSection[]): (DocSection & { id?: string; fromDb?: boolean })[] {
  if (!dbSections.length) return staticSections;

  const dbBySlug = new Map(dbSections.map((s) => [s.slug, s]));
  const merged: (DocSection & { id?: string; fromDb?: boolean })[] = [];
  const usedSlugs = new Set<string>();

  // DB sections first (sorted by sort_order)
  const sorted = [...dbSections].sort((a, b) => a.sort_order - b.sort_order);
  for (const db of sorted) {
    const staticMatch = staticSections.find((s) => s.slug === db.slug);
    merged.push({
      slug: db.slug,
      title: db.title,
      description: db.description,
      icon: staticMatch?.icon ?? resolveIcon(db.icon),
      content: db.content || staticMatch?.content || "",
      id: db.id,
      fromDb: true,
    });
    usedSlugs.add(db.slug);
  }

  // Append static sections not overridden
  for (const s of staticSections) {
    if (!usedSlugs.has(s.slug)) {
      merged.push(s);
    }
  }

  return merged;
}

export function useDocSections() {
  return useQuery({
    queryKey: ["doc-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("doc_sections")
        .select("*")
        .eq("published", true)
        .order("sort_order");

      if (error) {
        console.error("Failed to fetch doc sections:", error);
        return staticSections;
      }

      return mergeWithStatic(data as DbDocSection[]);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Admin: fetch ALL sections (including unpublished) */
export function useAdminDocSections() {
  return useQuery({
    queryKey: ["admin-doc-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("doc_sections")
        .select("*")
        .order("sort_order");

      if (error) throw error;
      return data as DbDocSection[];
    },
  });
}

export function useUpsertDocSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (section: Partial<DbDocSection> & { slug: string; title: string }) => {
      if (section.id) {
        const { error } = await supabase
          .from("doc_sections")
          .update({
            slug: section.slug,
            title: section.title,
            description: section.description ?? "",
            icon: section.icon ?? "FileText",
            content: section.content ?? "",
            sort_order: section.sort_order ?? 0,
            published: section.published ?? true,
            updated_at: new Date().toISOString(),
          })
          .eq("id", section.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("doc_sections").insert({
          slug: section.slug,
          title: section.title,
          description: section.description ?? "",
          icon: section.icon ?? "FileText",
          content: section.content ?? "",
          sort_order: section.sort_order ?? 0,
          published: section.published ?? true,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-sections"] });
      qc.invalidateQueries({ queryKey: ["admin-doc-sections"] });
    },
  });
}

export function useDeleteDocSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("doc_sections").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-sections"] });
      qc.invalidateQueries({ queryKey: ["admin-doc-sections"] });
    },
  });
}
